// Workflow-wide expression validation: walks every node's data for strings
// containing {{ and validates each against that node's context (reachable
// predecessors, trigger keys, loop variables). Pure — no fetching/mutation.

import type { Edge, Node } from '@xyflow/react'
import { buildParentIndex, loopVariablesAtNode, predecessorsOf } from './graphReachability'
import { validateExpressionText, ValidationIssue } from './validateExpression'
import type { TriggerNodeData } from '../types'

export interface NodeIssue extends ValidationIssue {
  nodeId: string
  /** Dotted path inside node.data, e.g. ``parameters.keyword.value``. */
  fieldPath: string
}

export interface WorkflowValidationResult {
  errors: NodeIssue[]
  warnings: NodeIssue[]
  /** Workflow calls functions.* but the registry hasn't loaded — show "couldn't verify". */
  functionsRegistryUnavailable: boolean
}

export interface WorkflowValidationInputs {
  nodes: Node[]
  edges: Edge[]
  variableNames: string[]
  functionNames: string[] | null
}

export const validateWorkflow = (inputs: WorkflowValidationInputs): WorkflowValidationResult => {
  const { nodes, edges, variableNames, functionNames } = inputs
  const parents = buildParentIndex(edges)
  const allNodeIds = nodes.map((n) => n.id)

  // A node only accepts keys of triggers it is wired to: at runtime the
  // payload comes from the calling trigger, so a key from an unconnected
  // trigger resolves to "". Same narrowing as the autocomplete.
  const triggerKeysByNode = new Map<string, string[]>()
  nodes.forEach((n) => {
    const data = n.data as TriggerNodeData
    if (data?.type === 'trigger' && data.triggerType === 'mcp') {
      const schema = data.mcpToolInputSchema as
        | { properties?: Record<string, unknown> }
        | undefined
      triggerKeysByNode.set(n.id, Object.keys(schema?.properties ?? {}))
    }
  })

  const errors: NodeIssue[] = []
  const warnings: NodeIssue[] = []
  let sawFunctionCall = false

  nodes.forEach((node) => {
    const reachableSet = predecessorsOf(node.id, parents)
    const reachable = Array.from(reachableSet).sort()
    const loopVars = loopVariablesAtNode(node.id, nodes, parents)
    const triggerKeys = new Set<string>()
    triggerKeysByNode.forEach((keys, triggerId) => {
      if (reachableSet.has(triggerId)) keys.forEach((k) => triggerKeys.add(k))
    })
    const triggerKeysList = Array.from(triggerKeys).sort()

    walkStrings(node.data, '', (text, fieldPath) => {
      if (!text.includes('{{')) return
      if (FUNCTION_REF_RE.test(text)) sawFunctionCall = true

      const issues = validateExpressionText(text, {
        availableNodeIds: reachable,
        variableNames,
        triggerKeys: triggerKeysList,
        nodeOutputKeys: {},
        loopVariableNames: loopVars,
        allNodeIds,
        functionNames,
      })

      issues.forEach((issue) => {
        const wrapped: NodeIssue = { ...issue, nodeId: node.id, fieldPath }
        if (issue.severity === 'error') errors.push(wrapped)
        else warnings.push(wrapped)
      })
    })
  })

  return {
    errors,
    warnings,
    functionsRegistryUnavailable: sawFunctionCall && functionNames === null,
  }
}

const FUNCTION_REF_RE = /\{\{\s*functions\./

// Visits every string in a nested object with its dotted path; arrays use key[index].
const walkStrings = (
  value: unknown,
  path: string,
  visit: (text: string, fieldPath: string) => void,
): void => {
  if (typeof value === 'string') {
    visit(value, path)
    return
  }
  if (Array.isArray(value)) {
    value.forEach((item, idx) => walkStrings(item, `${path}[${idx}]`, visit))
    return
  }
  if (value && typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      walkStrings(v, path ? `${path}.${k}` : k, visit)
    })
  }
}
